import React, { useEffect, useRef, useState } from "react";

/**
 * Reusable themed tabs.
 *
 * <Tabs
 *   tabs={[
 *     { key: "list", label: "Surah List", content: <SurahList /> },
 *     { key: "add", label: "Add Surah", icon: ic_edit, count: 3, content: <AddSurah /> },
 *     { key: "old", label: "Archived", disabled: true },
 *   ]}
 *   variant="solid"       // solid (default) | pill | line
 *   size="md"             // sm | md
 *   fullWidth={false}
 *   defaultActiveKey="list"
 *   activeKey={activeKey}          // pass with onChange for controlled use
 *   onChange={(key) => setActiveKey(key)}
 * />
 */
const Tabs = ({
  tabs = [],
  activeKey,
  defaultActiveKey,
  onChange,
  variant = "solid",
  size = "md",
  fullWidth = false,
  className = "",
  contentClassName = "",
  renderContent = true,
  id = "cx-tabs",
}) => {
  const isControlled = activeKey !== undefined && activeKey !== null;
  const firstEnabled = tabs.find((tab) => !tab.disabled);
  const [internalKey, setInternalKey] = useState(
    defaultActiveKey !== undefined && defaultActiveKey !== null
      ? defaultActiveKey
      : firstEnabled
      ? firstEnabled.key
      : null
  );
  const tabRefs = useRef([]);

  const currentKey = isControlled ? activeKey : internalKey;

  // Keep the selection valid when tabs are loaded/changed asynchronously.
  useEffect(() => {
    if (isControlled || !tabs.length) return;
    const stillExists = tabs.some(
      (tab) => tab.key === internalKey && !tab.disabled
    );
    if (!stillExists) {
      const next = tabs.find((tab) => !tab.disabled);
      setInternalKey(next ? next.key : null);
    }
  }, [tabs, internalKey, isControlled]);

  const selectTab = (tab) => {
    if (!tab || tab.disabled || tab.key === currentKey) return;
    if (!isControlled) setInternalKey(tab.key);
    onChange && onChange(tab.key, tab);
  };

  const handleKeyDown = (e, index) => {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const enabled = tabs
      .map((tab, i) => ({ tab, i }))
      .filter(({ tab }) => !tab.disabled);
    if (!enabled.length) return;

    const position = enabled.findIndex(({ i }) => i === index);
    let target;
    if (e.key === "Home") target = enabled[0];
    else if (e.key === "End") target = enabled[enabled.length - 1];
    else if (e.key === "ArrowRight")
      target = enabled[(position + 1) % enabled.length];
    else target = enabled[(position - 1 + enabled.length) % enabled.length];

    selectTab(target.tab);
    const node = tabRefs.current[target.i];
    node && node.focus();
  };

  const activeTab = tabs.find((tab) => tab.key === currentKey);

  return (
    <div className={`cx-tabs cx-tabs-${variant} cx-tabs-${size} ${className}`}>
      <div
        className={`cx-tabs-nav ${fullWidth ? "cx-tabs-full" : ""}`}
        role="tablist"
      >
        {tabs.map((tab, index) => {
          const isActive = tab.key === currentKey;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`${id}-tab-${tab.key}`}
              aria-controls={`${id}-panel-${tab.key}`}
              aria-selected={isActive}
              aria-disabled={tab.disabled ? true : undefined}
              disabled={tab.disabled}
              tabIndex={isActive ? 0 : -1}
              ref={(node) => (tabRefs.current[index] = node)}
              className={`cx-tab ${isActive ? "cx-tab-active" : ""}`}
              onClick={() => selectTab(tab)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              {tab.icon &&
                (typeof tab.icon === "string" ? (
                  <img src={tab.icon} alt="" className="cx-tab-icon" />
                ) : (
                  <span className="cx-tab-icon">{tab.icon}</span>
                ))}
              <span className="cx-tab-label">{tab.label}</span>
              {(tab.count || tab.count === 0) && (
                <span className="cx-tab-count">{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {renderContent && activeTab && activeTab.content !== undefined && (
        <div
          role="tabpanel"
          id={`${id}-panel-${activeTab.key}`}
          aria-labelledby={`${id}-tab-${activeTab.key}`}
          className={`cx-tabs-panel ${contentClassName}`}
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
};

export default Tabs;
